// CB:BEGIN service-area-setup
/**
 * ServiceAreaSetup
 *
 * Contractor service area configuration:
 * - Base address (line1, city, state, zip)
 * - Service radius (miles)
 *
 * Used in:
 *  - ContractorOnboarding.tsx (onboarding step)
 *  - Contractor settings/profile page
 *
 * On save: calls PUT /api/contractors/service-area
 * On load: calls GET /api/contractors/service-area
 */
import React, { useEffect, useState } from 'react';
import { useApi } from '../lib/useApi';

const RADIUS_OPTIONS = [5, 10, 15, 25, 50, 75, 100] as const;
const DEFAULT_RADIUS = 25;

export interface ServiceAreaData {
  baseAddressLine1: string | null;
  baseCity: string | null;
  baseState: string | null;
  baseZip: string | null;
  baseLat: number | null;
  baseLng: number | null;
  bidRadiusMiles: number;
  hasServiceArea: boolean;
}

interface Props {
  /** If true, shows a compact card without heading */
  compact?: boolean;
  /** Called after a successful save */
  onSaved?: (data: ServiceAreaData) => void;
  /** If true, show a "Skip for now" secondary button (e.g. onboarding) */
  showSkip?: boolean;
  /** Called when user clicks Skip (required if showSkip is true) */
  onSkip?: () => void;
  className?: string;
}

export function ServiceAreaSetup({ compact = false, onSaved, showSkip = false, onSkip, className = '' }: Props) {
  const { get, put } = useApi();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [radius, setRadius] = useState<number>(DEFAULT_RADIUS);

  // Existing config (if any)
  const [existing, setExisting] = useState<ServiceAreaData | null>(null);

  useEffect(() => {
    setLoading(true);
    get('/api/contractors/service-area')
      .then((data: any) => {
        setExisting(data as ServiceAreaData);
        setLine1(data.baseAddressLine1 ?? '');
        setCity(data.baseCity ?? '');
        setState(data.baseState ?? '');
        setZip(data.baseZip ?? '');
        setRadius(data.bidRadiusMiles ?? DEFAULT_RADIUS);
      })
      .catch(() => {
        // No existing data — defaults are fine
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!line1.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setError('All address fields are required.');
      return;
    }

    setSaving(true);
    try {
      const result: any = await put('/api/contractors/service-area', {
        line1: line1.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        radiusMiles: radius,
      });

      setSuccess(true);
      setExisting({
        baseAddressLine1: line1.trim(),
        baseCity: city.trim(),
        baseState: state.trim(),
        baseZip: zip.trim(),
        baseLat: result.baseLat ?? null,
        baseLng: result.baseLng ?? null,
        bidRadiusMiles: radius,
        hasServiceArea: true,
      });
      onSaved?.({
        baseAddressLine1: line1.trim(),
        baseCity: city.trim(),
        baseState: state.trim(),
        baseZip: zip.trim(),
        baseLat: result.baseLat ?? null,
        baseLng: result.baseLng ?? null,
        bidRadiusMiles: radius,
        hasServiceArea: true,
      });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save service area. Please verify your address and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse space-y-3 ${className}`}>
        <div className="h-10 bg-gray-100 rounded-lg" />
        <div className="h-10 bg-gray-100 rounded-lg" />
        <div className="h-10 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14, color: '#374151' };
  const requiredStar = <span style={{ color: '#dc2626' }}> *</span>;

  return (
    <div className={className}>
      {!compact && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Service Area</h3>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
            Set your base of operations and the radius you're willing to travel. Only projects within
            this area will be shown to you.
          </p>
        </div>
      )}

      {/* Existing config summary */}
      {existing?.hasServiceArea && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 14, color: '#065f46', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width={18} height={18} viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>
            Service area set: <strong>{existing.bidRadiusMiles} mi</strong> radius from{' '}
            {[existing.baseAddressLine1, existing.baseCity, existing.baseState].filter(Boolean).join(', ')}
          </span>
        </div>
      )}

      <div style={{ padding: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Base address */}
          <div>
            <label style={labelStyle}>Street Address{requiredStar}</label>
            <input
              type="text"
              value={line1}
              onChange={(e) => setLine1(e.target.value)}
              placeholder="123 Main St"
              className="form-control"
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
            <div>
              <label style={labelStyle}>City{requiredStar}</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Nashville"
                className="form-control"
                required
              />
            </div>
            <div>
              <label style={labelStyle}>State{requiredStar}</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="TN"
                maxLength={2}
                className="form-control"
                style={{ textTransform: 'uppercase' }}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>ZIP{requiredStar}</label>
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="37201"
                maxLength={5}
                className="form-control"
                required
              />
            </div>
          </div>

          {/* Radius picker */}
          <div>
            <label style={labelStyle}>Service Radius{requiredStar}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {RADIUS_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRadius(r)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 20,
                    fontSize: 14,
                    fontWeight: 500,
                    border: radius === r ? '2px solid #2563eb' : '1px solid #d1d5db',
                    background: radius === r ? '#2563eb' : '#fff',
                    color: radius === r ? '#fff' : '#374151',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background 0.2s, color 0.2s',
                  }}
                >
                  {r} mi
                </button>
              ))}
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
              Projects within <strong style={{ color: '#374151' }}>{radius} miles</strong> of your base address will be shown to you.
            </p>
          </div>

          {/* Error / success */}
          {error && (
            <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14, color: '#991b1b' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 14, color: '#065f46', display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width={18} height={18} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Service area saved successfully!
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button type="submit" disabled={saving} className="btn btn-primary" style={{ width: '100%' }}>
              {saving ? 'Saving...' : 'Save Service Area'}
            </button>
            {showSkip && onSkip && (
              <button type="button" onClick={onSkip} className="btn btn-secondary" style={{ width: '100%' }}>
                Skip for now
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ServiceAreaSetup;
// CB:END service-area-setup
